import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import { motion } from "framer-motion";
import KPICard from "@/components/KPICard";
import { Trophy, ChartLineUp, ClockClockwise, CurrencyCircleDollar, CaretUp, CaretDown, CaretUpDown } from "@phosphor-icons/react";
import FilterBar, { emptyFilters, filtersToParams } from "@/components/FilterBar";

const COLORS = ["#002FA7", "#D97706", "#4F46E5", "#059669", "#DC2626", "#0EA5E9", "#A855F7"];

export default function Analytics() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(emptyFilters());
  const [options, setOptions] = useState(null);
  // Leaderboard sort: default by count desc (matches backend default).
  const [sort, setSort] = useState({ key: "count", dir: "desc" });

  const load = (f) => {
    setLoading(true);
    api.get("/analytics/summary", { params: filtersToParams(f) })
      .then((r) => setStats(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/reports/options").then((r) => setOptions(r.data)).catch(() => {});
    load(emptyFilters());
  }, []);

  const onApply = (next) => {
    setFilters(next);
    load(next);
  };

  // Derive sorted attorney rows. Win rate is computed client-side.
  const sortedAttorneys = useMemo(() => {
    const rows = (stats?.by_attorney || []).map((a) => {
      const closed = (a.won || 0) + (a.lost || 0);
      const winRate = closed > 0 ? (a.won / closed) * 100 : -1; // -1 keeps "—" rows at the bottom of desc sort
      return { ...a, _closed: closed, _winRate: winRate };
    });
    const key = sort.key;
    const dir = sort.dir === "asc" ? 1 : -1;
    const getVal = (r) => {
      switch (key) {
        case "attorney": return (r.attorney || "").toLowerCase();
        case "count": return r.count || 0;
        case "submitted": return r.submitted || 0;
        case "won": return r.won || 0;
        case "lost": return r.lost || 0;
        case "winRate": return r._winRate;
        default: return 0;
      }
    };
    return rows.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [stats, sort]);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) {
        // First click on a new numeric column → desc (high→low) so managers
        // see top performers first. Attorney name defaults to asc.
        return { key, dir: key === "attorney" ? "asc" : "desc" };
      }
      return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const SortIcon = ({ colKey }) => {
    if (sort.key !== colKey) return <CaretUpDown size={12} className="inline ml-1 opacity-40" />;
    return sort.dir === "asc"
      ? <CaretUp size={12} className="inline ml-1" weight="fill" />
      : <CaretDown size={12} className="inline ml-1" weight="fill" />;
  };

  const SortHeader = ({ colKey, align = "right", title, children }) => (
    <th
      className={`py-2 label-tracking !tracking-[0.18em] ${align === "right" ? "text-right" : ""} cursor-pointer select-none hover:text-slate-900`}
      title={title || "Click to sort"}
      onClick={() => toggleSort(colKey)}
      data-testid={`leaderboard-sort-${colKey}`}
      aria-sort={sort.key === colKey ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      {children}
      <SortIcon colKey={colKey} />
    </th>
  );

  const formatWeekLabel = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso + "T00:00:00Z");
      return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
    } catch {
      return iso;
    }
  };

  if (loading || !stats) {
    return <div className="text-sm text-slate-500" data-testid="analytics-loading">Loading analytics…</div>;
  }

  const funnelData = [
    { name: "New", value: stats.by_status.new, fill: "#002FA7" },
    { name: "Contacted", value: stats.by_status.contacted, fill: "#D97706" },
    { name: "In Progress", value: stats.by_status.in_progress || 0, fill: "#0EA5E9" },
    { name: "Qualified", value: stats.by_status.qualified, fill: "#4F46E5" },
    { name: "Won", value: stats.by_status.won, fill: "#059669" },
    { name: "Lost", value: stats.by_status.lost, fill: "#DC2626" },
  ];

  return (
    <div data-testid="analytics-page" className="space-y-10">
      <div>
        <div className="label-tracking">Performance</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none mt-2 text-slate-900">
          Analytics.
        </h1>
        <p className="text-sm text-slate-500 mt-3">Win rate, sources, attorney leaderboard, and pipeline velocity.</p>
      </div>

      <FilterBar value={filters} onApply={onApply} options={options} showJobTitles testId="analytics-filters" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <KPICard testId="metric-conversion" label="Conversion Rate" value={`${stats.conversion_rate}%`} accent="text-emerald-700" icon={ChartLineUp} index={0} sub={`${stats.by_status.won} won of ${stats.by_status.won + stats.by_status.lost} closed`} />
        <KPICard testId="metric-total-won" label="Total Won" value={stats.by_status.won} icon={Trophy} index={1} />
        <KPICard testId="metric-avg-close" label="Avg Days to Close" value={stats.avg_days_to_close ?? "—"} icon={ClockClockwise} index={2} />
        <KPICard testId="metric-revenue" label="Won Value (ZAR)" value={`R${(stats.total_won_value || 0).toLocaleString()}`} icon={CurrencyCircleDollar} index={3} accent="text-emerald-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white border border-slate-200 rounded-sm p-6"
          data-testid="chart-funnel"
        >
          <div className="label-tracking mb-1">Pipeline Funnel</div>
          <h3 className="font-display text-xl text-slate-900 mb-4">Status distribution</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={funnelData} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} />
              <Tooltip cursor={{ fill: "rgba(15,23,42,0.04)" }} contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 4, fontSize: 12 }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="bg-white border border-slate-200 rounded-sm p-6"
          data-testid="chart-source"
        >
          <div className="label-tracking mb-1">Channels</div>
          <h3 className="font-display text-xl text-slate-900 mb-4">Leads by source</h3>
          {stats.by_source.length === 0 ? (
            <div className="h-[280px] flex items-center justify-center text-sm text-slate-400">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={stats.by_source}
                  dataKey="count"
                  nameKey="source"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  innerRadius={45}
                  paddingAngle={2}
                >
                  {stats.by_source.map((entry, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 4, fontSize: 12 }} />
                <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="bg-white border border-slate-200 rounded-sm p-6 lg:col-span-2"
          data-testid="chart-timeline"
        >
          <div className="label-tracking mb-1">Volume</div>
          <h3 className="font-display text-xl text-slate-900 mb-4">Leads over time <span className="text-xs font-normal text-slate-400 ml-2">(last 14 weeks)</span></h3>
          {stats.by_day.length === 0 ? (
            <div className="h-[280px] flex items-center justify-center text-sm text-slate-400">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={stats.by_day} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="date" stroke="#64748B" fontSize={11} tickFormatter={formatWeekLabel} />
                <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 4, fontSize: 12 }}
                  labelFormatter={(v) => `Week of ${formatWeekLabel(v)}`}
                />
                <Line type="monotone" dataKey="count" stroke="#002FA7" strokeWidth={2} dot={{ fill: "#002FA7", r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="bg-white border border-slate-200 rounded-sm p-6 lg:col-span-2"
          data-testid="attorney-leaderboard"
        >
          <div className="label-tracking mb-1">Performance</div>
          <h3 className="font-display text-xl text-slate-900 mb-4">Attorney leaderboard</h3>
          {stats.by_attorney.length === 0 ? (
            <p className="text-sm text-slate-500">No leads have been assigned to attorneys yet.</p>
          ) : (
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm min-w-[600px]">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500">
                    <SortHeader colKey="attorney" align="left" title="Sort by name">Attorney</SortHeader>
                    <SortHeader colKey="count" title="Leads currently assigned to this attorney">Assigned</SortHeader>
                    <SortHeader colKey="submitted" title="Leads this attorney forwarded / submitted to Leads@">Submitted</SortHeader>
                    <SortHeader colKey="won">Won</SortHeader>
                    <SortHeader colKey="lost">Lost</SortHeader>
                    <SortHeader colKey="winRate">Win Rate</SortHeader>
                  </tr>
                </thead>
                <tbody>
                  {sortedAttorneys.map((a, i) => {
                    const closed = a._closed;
                    const wr = closed > 0 ? Math.round((a.won / closed) * 100) : 0;
                    return (
                      <tr key={a.upn || i} className="border-b border-slate-100" data-testid={`attorney-row-${a.upn || i}`}>
                        <td className="py-3 text-slate-900">{a.attorney}</td>
                        <td className="py-3 text-right font-mono">{a.count}</td>
                        <td className="py-3 text-right font-mono text-sky-700" data-testid={`attorney-submitted-${a.upn || i}`}>{a.submitted || 0}</td>
                        <td className="py-3 text-right font-mono text-emerald-700">{a.won}</td>
                        <td className="py-3 text-right font-mono text-rose-700">{a.lost}</td>
                        <td className="py-3 text-right font-mono">{closed > 0 ? `${wr}%` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </motion.section>
      </div>

      {stats.by_lost_reason && stats.by_lost_reason.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.45 }}
          className="bg-white border border-slate-200 rounded-sm p-6"
          data-testid="by-lost-reason-section"
        >
          <div className="label-tracking mb-4">Why we lose leads</div>
          <ul className="space-y-2">
            {stats.by_lost_reason.map((r, i) => {
              const max = stats.by_lost_reason[0].count || 1;
              const pct = Math.round((r.count / max) * 100);
              return (
                <li key={i} className="flex items-center gap-3 min-w-0" data-testid={`lost-reason-bar-${i}`}>
                  <span className="text-sm text-slate-700 w-32 sm:w-64 shrink-0 truncate">{r.reason}</span>
                  <div className="flex-1 min-w-0 h-2 bg-rose-50 rounded-sm overflow-hidden">
                    <div className="h-full bg-rose-500" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-mono text-rose-700 w-8 text-right shrink-0">{r.count}</span>
                </li>
              );
            })}
          </ul>
        </motion.section>
      )}
    </div>
  );
}
